import { useEffect, useState } from "react";
import { useParams, Navigate } from "react-router-dom";
import { usePage } from "@/contexts/PageContext";
import { CheckoutFlow } from "./CheckoutFlow";

export function CheckoutRoute() {
  const { checkoutId, step } = useParams<{ checkoutId: string; step: string }>();
  const { setStripe, setCheckoutSettings } = usePage();
  const [data, setData] = useState<{
    products: any[];
    lineItems: Array<{ productId: string; variantId?: string }>;
    checkoutSession?: {
      customerEmail?: string;
      customerPhone?: string;
      shipping?: {
        name: string;
        address: {
          line1: string;
          line2?: string;
          city: string;
          state: string;
          country: string;
          postal_code: string;
        };
      };
      contactCustomFields?: Array<{
        key: string;
        label: string;
        required?: boolean;
        options: string[];
      }>;
      contactCustomFieldValues?: Record<string, string>;
    };
    stripe: { publishableKey: string; accountId?: string } | null;
    checkoutSettings: any;
  } | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!checkoutId) return;

    const fetchCheckout = async () => {
      try {
        const response = await fetch(`/api/checkout/${checkoutId}`);
        if (response.ok) {
          const result = await response.json();
          const c = result.checkout;
          setData({
            products: result.products || [],
            lineItems: c?.lineItems || [],
            checkoutSession: {
              customerEmail: c?.customerEmail,
              customerPhone: c?.customerPhone,
              shipping: c?.shipping,
              contactCustomFields: c?.contactCustomFields,
              contactCustomFieldValues: c?.contactCustomFieldValues,
            },
            stripe: result.stripe || null,
            checkoutSettings: result.checkoutSettings || null,
          });
          if (result.stripe) {
            setStripe(result.stripe);
          }
          if (result.checkoutSettings) {
            setCheckoutSettings(result.checkoutSettings);
          }
        }
      } catch (error) {
        console.error("Error fetching checkout:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchCheckout();
  }, [checkoutId, setStripe, setCheckoutSettings]);

  // Redirect /checkout/:checkoutId to /checkout/:checkoutId/contact
  if (checkoutId && !step) {
    return <Navigate to={`/checkout/${checkoutId}/contact`} replace />;
  }

  if (checkoutId && step === "email") {
    return <Navigate to={`/checkout/${checkoutId}/contact`} replace />;
  }

  if (loading || !data || !checkoutId) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }

  const validSteps = ["contact", "shipping", "payment"];
  if (step && !validSteps.includes(step)) {
    return <Navigate to={`/checkout/${checkoutId}/contact`} replace />;
  }

  return (
    <CheckoutFlow
      checkoutId={checkoutId}
      products={data.products}
      lineItems={data.lineItems}
      checkoutSession={data.checkoutSession}
      stripe={data.stripe}
      checkoutSettings={data.checkoutSettings}
    />
  );
}
